import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type Ref,
} from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import {
  ELEVATED_DIALOG_TOOLTIP_Z_INDEX_OFFSET,
  ELEVATED_DIALOG_Z_INDEX_OFFSET,
} from "../../constants/dialogZIndex";

const CONCEPT_CHIP_GAP_PX = 4;
const ROW_ELLIPSIS_PX = 16;

const AI_IMPACTED_CONCEPTS_CELL_ROOT_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  height: "100%",
  width: "100%",
  minHeight: 0,
  boxSizing: "border-box" as const,
} as const;

const CONCEPT_CHIP_OUTER_SX = {
  boxSizing: "border-box" as const,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  minWidth: 39,
  height: 24,
  px: "10px",
  py: "4px",
  borderRadius: "9999px",
  backgroundColor: "#F2F2F2",
  color: "#626F86",
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 700,
  fontSize: 12,
  lineHeight: "16px",
  letterSpacing: 0,
  textTransform: "capitalize" as const,
} as const;

const CONCEPT_CHIP_LABEL_SX = {
  display: "block",
  minWidth: 0,
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const CONCEPT_CHIP_TOOLTIP_SLOT_PROPS = {
  popper: {
    sx: {
      zIndex: (theme: { zIndex: { modal: number } }) =>
        theme.zIndex.modal +
        ELEVATED_DIALOG_Z_INDEX_OFFSET +
        ELEVATED_DIALOG_TOOLTIP_Z_INDEX_OFFSET,
    },
  },
} as const;

function parseAiImpactedConcepts(raw: unknown): string[] {
  if (typeof raw !== "string") return [];
  const t = raw.trim();
  if (!t || t === "—") return [];
  return t
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function computeConceptsFit(
  widths: number[],
  containerWidth: number,
): { visible: number; needsRowEllipsis: boolean } {
  const n = widths.length;
  if (n === 0) return { visible: 0, needsRowEllipsis: false };
  if (containerWidth <= 0) return { visible: n, needsRowEllipsis: false };

  const gap = CONCEPT_CHIP_GAP_PX;
  const ell = ROW_ELLIPSIS_PX;

  const sumWidth = (count: number) => {
    let s = 0;
    for (let i = 0; i < count; i++) {
      s += widths[i];
      if (i > 0) s += gap;
    }
    return s;
  };

  if (sumWidth(n) <= containerWidth) {
    return { visible: n, needsRowEllipsis: false };
  }

  for (let k = n; k >= 1; k--) {
    const base = sumWidth(k);
    const needEllipsis = k < n;
    const total = needEllipsis ? base + gap + ell : base;
    if (total <= containerWidth) {
      return { visible: k, needsRowEllipsis: needEllipsis };
    }
  }

  return { visible: 1, needsRowEllipsis: n > 1 };
}

function AiConceptChip({
  label,
  chipRef,
  constrainWidth = false,
}: Readonly<{
  label: string;
  chipRef?: Ref<HTMLSpanElement>;
  constrainWidth?: boolean;
}>) {
  const labelRef = useRef<HTMLSpanElement>(null);
  const [labelTruncated, setLabelTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = labelRef.current;
    if (!el) {
      setLabelTruncated(false);
      return;
    }
    const measure = () => {
      setLabelTruncated(el.scrollWidth > el.clientWidth + 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [label, constrainWidth]);

  const chip = (
    <Box
      component="span"
      ref={chipRef}
      sx={{
        ...CONCEPT_CHIP_OUTER_SX,
        ...(constrainWidth
          ? { flexShrink: 1, minWidth: 0, maxWidth: "100%" }
          : {}),
      }}
    >
      <Box component="span" ref={labelRef} sx={CONCEPT_CHIP_LABEL_SX}>
        {label}
      </Box>
    </Box>
  );

  return (
    <Tooltip
      title={label}
      arrow
      placement="top"
      disableInteractive
      disableHoverListener={!labelTruncated}
      slotProps={CONCEPT_CHIP_TOOLTIP_SLOT_PROPS}
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          maxWidth: constrainWidth ? "100%" : undefined,
          minWidth: 0,
        }}
      >
        {chip}
      </Box>
    </Tooltip>
  );
}

/** Chip row for Impacted Concepts — shared by Assessment Center grids and Start Manual Assessment. */
export function AiImpactedConceptsCell({ value }: Readonly<{ value: unknown }>) {
  const concepts = useMemo(() => parseAiImpactedConcepts(value), [value]);
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState({
    visible: concepts.length,
    rowEllipsis: false,
  });

  useLayoutEffect(() => {
    if (concepts.length === 0) return;

    const run = () => {
      const container = containerRef.current;
      const measure = measureRef.current;
      if (!container || !measure) return;

      const cw = container.clientWidth;
      const children = Array.from(measure.children) as HTMLElement[];
      const widths = children.map((c) =>
        Math.ceil(c.getBoundingClientRect().width),
      );

      const next = computeConceptsFit(widths, cw);
      setFit((prev) =>
        prev.visible === next.visible &&
        prev.rowEllipsis === next.needsRowEllipsis
          ? prev
          : {
              visible: next.visible,
              rowEllipsis: next.needsRowEllipsis,
            },
      );
    };

    run();
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(run);
    ro.observe(el);
    return () => ro.disconnect();
  }, [concepts]);

  if (concepts.length === 0) {
    return (
      <Box sx={{ ...AI_IMPACTED_CONCEPTS_CELL_ROOT_SX, color: "text.secondary" }}>
        —
      </Box>
    );
  }

  const fullTitle = concepts.join(", ");
  const visibleConcepts = concepts.slice(
    0,
    Math.min(fit.visible, concepts.length),
  );
  const singleVisibleChip =
    visibleConcepts.length === 1 && !fit.rowEllipsis;

  const chipRow = (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        minWidth: 0,
        height: "100%",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      <Box
        ref={measureRef}
        aria-hidden
        sx={{
          position: "absolute",
          left: -99999,
          top: 0,
          display: "flex",
          gap: `${CONCEPT_CHIP_GAP_PX}px`,
          alignItems: "center",
          pointerEvents: "none",
        }}
      >
        {concepts.map((c, i) => (
          <AiConceptChip key={`m-${i}-${c}`} label={c} />
        ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: `${CONCEPT_CHIP_GAP_PX}px`,
          alignItems: "center",
          minWidth: 0,
          overflow: "hidden",
          width: "100%",
        }}
      >
        {visibleConcepts.map((c, i) => (
          <AiConceptChip
            key={`v-${i}-${c}`}
            label={c}
            constrainWidth={singleVisibleChip}
          />
        ))}
        {fit.rowEllipsis ? (
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              fontSize: 12,
              lineHeight: "16px",
              color: "#626F86",
              fontWeight: 700,
            }}
          >
            ...
          </Box>
        ) : null}
      </Box>
    </Box>
  );

  const content = !fit.rowEllipsis ? (
    chipRow
  ) : (
    <Tooltip
      title={fullTitle}
      arrow
      placement="top"
      disableInteractive
      slotProps={CONCEPT_CHIP_TOOLTIP_SLOT_PROPS}
    >
      <Box
        component="span"
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          minWidth: 0,
          height: "100%",
        }}
      >
        {chipRow}
      </Box>
    </Tooltip>
  );

  return <Box sx={AI_IMPACTED_CONCEPTS_CELL_ROOT_SX}>{content}</Box>;
}
